import React, {Fragment, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {Loader2, X} from 'lucide-react';
import {toast} from 'react-toastify';
import AdminBioMatchFulfillmentService from '../services/adminBioMatchFulfillmentService';
import {logError} from '@/shared/utils/logger';

/**
 * Admin: manually add a blood sample for a patient. Substitute for
 * real OHD plumbing until the OHD sync surfaces sample-type +
 * collected_at — same dataAccess insert path, so OHD sync slots in
 * later without changing this UI.
 */

interface Props {
    open: boolean;
    onClose: () => void;
    onCreated?: () => void;
}

const SAMPLE_TYPES: Array<{value: 'whole_blood' | 'finger_prick' | 'dried_blood_spot' | 'other'; label: string}> = [
    {value: 'whole_blood', label: 'Whole blood (veinal)'},
    {value: 'finger_prick', label: 'Finger prick'},
    {value: 'dried_blood_spot', label: 'Dried blood spot'},
    {value: 'other', label: 'Other'},
];

const AdminAddBloodSampleModal: React.FC<Props> = ({open, onClose, onCreated}) => {
    const [submitting, setSubmitting] = useState(false);
    const [userId, setUserId] = useState('');
    const [sampleType, setSampleType] = useState<typeof SAMPLE_TYPES[number]['value']>('whole_blood');
    const [collectedAtLocal, setCollectedAtLocal] = useState(() => {
        // Default to "now" in the admin's local time, but submit as
        // UTC so the BE storage matches what the patient browser
        // expects from the countdown timer.
        const d = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    });
    const [viabilityHours, setViabilityHours] = useState(72);
    const [notes, setNotes] = useState('');

    const submit = async () => {
        const userIdNum = parseInt(userId, 10);
        if (!Number.isFinite(userIdNum)) {
            toast.error('Enter a numeric user_id.');
            return;
        }
        const collectedAt = new Date(collectedAtLocal);
        if (Number.isNaN(collectedAt.getTime())) {
            toast.error('Enter a valid collection timestamp.');
            return;
        }
        setSubmitting(true);
        try {
            await AdminBioMatchFulfillmentService.createBloodSample({
                user_id: userIdNum,
                sample_type: sampleType,
                collected_at: collectedAt.toISOString(),
                viability_hours: viabilityHours,
                notes: notes || undefined,
            });
            toast.success('Blood sample recorded');
            setUserId('');
            setNotes('');
            onCreated?.();
            onClose();
        } catch (err) {
            logError('Create blood sample failed', err);
            toast.error('Could not save blood sample.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={submitting ? () => undefined : onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/40" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex items-start justify-between gap-3">
                                    <Dialog.Title className="text-lg font-semibold text-mld-text-primary">
                                        Record blood sample
                                    </Dialog.Title>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={submitting}
                                        aria-label="Close"
                                        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50">
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>

                                <p className="mt-2 text-xs text-mld-text-secondary">
                                    Manual entry until OHD plumbing lands. Sample type and collection time drive the
                                    patient-side viability countdown.
                                </p>

                                <div className="mt-4 space-y-3">
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-gray-700">User ID</label>
                                        <input
                                            type="number"
                                            value={userId}
                                            onChange={(e) => setUserId(e.target.value)}
                                            placeholder="e.g. 72768"
                                            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-gray-700">Sample type</label>
                                        <select
                                            value={sampleType}
                                            onChange={(e) => setSampleType(e.target.value as typeof sampleType)}
                                            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary">
                                            {SAMPLE_TYPES.map((s) => (
                                                <option key={s.value} value={s.value}>
                                                    {s.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-gray-700">
                                            Collected at (your local time)
                                        </label>
                                        <input
                                            type="datetime-local"
                                            value={collectedAtLocal}
                                            onChange={(e) => setCollectedAtLocal(e.target.value)}
                                            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-gray-700">
                                            Viability (hours)
                                        </label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={720}
                                            value={viabilityHours}
                                            onChange={(e) => setViabilityHours(parseInt(e.target.value, 10) || 72)}
                                            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-xs font-medium text-gray-700">
                                            Notes (optional)
                                        </label>
                                        <input
                                            type="text"
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                        />
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={submitting}
                                        className="rounded-full border border-mld-panel-border px-4 py-2 text-sm font-medium text-mld-text-primary hover:bg-mld-panel-bg disabled:opacity-50">
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={submit}
                                        disabled={submitting}
                                        className="inline-flex items-center gap-2 rounded-full bg-mld-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60">
                                        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                                        Save sample
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default AdminAddBloodSampleModal;
